import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { SimpleFormLabel } from "components/dialogs/SimpleFormDialog";
import { Divider, Stack } from "@mui/material";
import { Agg } from "system/query/Aggs";
import LeafAggSelector from "components/widgetParts/LeafAggSelector";
import { QueryFieldSelector } from "components/queries/queryBuilder/dialogParts/QueryFieldSelector";
import { QueryField, QueryFieldTypes, QueryInfo, socialUniverseField } from "system/query/QueryInfo";
import TabbedDialog from "components/dialogs/TabbedDialog";
import SplitSettingsWrapper from "components/settingsConfig/SplitSettingsWrapper";
import SettingsOptions from "components/settingsConfig/SettingsOptions";
import PercentChangePreview from "./PercentChangePreview";
import SettingsAccordion from "components/settingsConfig/uiComponents/SettingsAccordion";
import SettingsWidgetTitleSelect from "components/settingsConfig/uiComponents/SettingsWidgetTitleSingleSelect";
import SettingsTextField from "components/settingsConfig/uiComponents/SettingsTextField";
import SettingsToggleButton, { horizontalAlignmentOptions } from "components/settingsConfig/uiComponents/SettingsToggleButton";
import { AutoWidgetQueryBehavior, getSingleOriginalRequestByName, WidgetRequest } from "system/widget/Widget";
import { WidgetSettingsProps } from "components/widgetParts/WidgetSettingsWrapper";

export type PercentChangeSettingsStateType = {
  customWidgetTitle: string;
  widgetTitle: string;
  widgetTitleAlignment: string;
};

export const defaultSettings: PercentChangeSettingsStateType = {
  customWidgetTitle: '',
  widgetTitle: 'default',
  widgetTitleAlignment: 'left',
};

const defaultWidgetRequest = {
  name: 'percent-change',
  endpoint: 'query/agg',
  queryParams: {
    flatten: true,
    include_total: true
  },
  autoWidgetQueryBehavior: AutoWidgetQueryBehavior.AUTO_SINGLE
} as WidgetRequest;

export function buildPercentChangeRequest(timestampField: string, timestampName: string, dataAgg?: Agg, dataName?: string): WidgetRequest {
  const widgetRequest : WidgetRequest = { ...defaultWidgetRequest };
  const requestInitBody = { aggs: {} };


  const name = dataName || dataAgg?.op;
  if (dataAgg && name) {
    requestInitBody.aggs = {
      [timestampName]: {
        "op": "histogram",
        "field": timestampField,
        "aggs": {
          [name]: dataAgg
        }
      }
    };
  } else {
    requestInitBody.aggs = {
      [timestampName]: {
        "op": "histogram",
        "field": timestampField
      }
    };
  }

  widgetRequest.requestInit = { body: requestInitBody };
  return widgetRequest;
}

export function BuildDefaultPercentChangeRequests(queryInfo: QueryInfo) : WidgetRequest[] | undefined {
  if (!queryInfo.fields) { return; } // We have a bug that fires this function before we have queryInfo.
  const widgetRequest : WidgetRequest = { ...defaultWidgetRequest };
  const requestInitBody = { aggs: {} };

  const foundTimestampField = queryInfo.fields?.find((field:QueryField) => field.type === QueryFieldTypes.timestamp) || false;
  const foundSocialUniverseField = queryInfo.fields?.find((field: QueryField) => field.id === socialUniverseField.id) || false;

  if (foundTimestampField) {
    requestInitBody.aggs = {
      histogram: {
        op: "histogram",
        field: foundTimestampField.id,
        ...(foundSocialUniverseField && {
          aggs: {
            [socialUniverseField.label]: {
              op: socialUniverseField.id
            }
          }
        }),
      },
    };
  }

  widgetRequest.requestInit = { body: requestInitBody };
  return [widgetRequest];
}


export default function PercentChangeSettings(props: WidgetSettingsProps): React.JSX.Element {
  const { i18n, t } = useTranslation();

  const originalRequest = getSingleOriginalRequestByName(props.activeWidgetRequests, 'percent-change');
  const requestBody = originalRequest?.requestInit?.body || {};

  const timestampAggDefinition = requestBody?.aggs ?? {};
  const timestampAggKey: string | undefined = Object.keys(timestampAggDefinition)[0];
  const timestampAgg: Agg | undefined = timestampAggDefinition[timestampAggKey];

  const [timestampFieldId, setTimestampFieldId] = useState<string | undefined>(timestampAgg?.field);

  const timestampField = useMemo(() => {
    const timestampField = props.queryInfo?.fields.find((field: QueryField) => {
      return field.id === timestampFieldId;
    });
    return timestampField;
  }, [timestampFieldId, props.queryInfo]);

  const dataAggDefinition: any = timestampAgg?.aggs ?? {};
  const dataAggKey: string | undefined = Object.keys(dataAggDefinition)[0];
  const [dataAgg, setDataAgg] = useState<Agg | undefined>(dataAggDefinition[dataAggKey]);
  const dataField = useMemo(() => {
    const dataField = props.queryInfo?.fields.find((field: QueryField) => {
      // Social Universe has a `null` field.  Instead it uses the `op` field as an identifier.
      return (field.id === dataAgg?.field) || (field.id === dataAgg?.op);
    });
    return dataField;
  }, [dataAgg, props.queryInfo]);

  const updateWidget = () => {
    if (!timestampField) {
      return;
    }
    const widgetRequest = buildPercentChangeRequest(timestampField.id, timestampField.label, dataAgg, dataField?.label);
    props.saveWidgetDataAndRequests({settings: settingsState}, [widgetRequest]).then(() => {
      if (props.widgetRefresh) {
        props.widgetRefresh();
      }
    })
  };

  // function saveSettings() {
  //   if (timestampField) {
  //     const requestBody = buildPercentChangeRequest(timestampField.id, timestampField.label, dataAgg, dataField?.label);
  //     props.saveRequestBodyToWidget(requestBody);
  //   }
  // }

  // const updateWidgetData = () => {
  //   if (props.widget?.id && props.onUpdateWidgetData) {
  //     props.onUpdateWidgetData(props.widget?.id, {
  //       settings: settingsState,
  //     }).then(() => {
  //       if (props.widgetRefresh) {
  //         props.widgetRefresh();
  //       }
  //     });
  //   }
  // };

  const [settingsState, setSettingsState] = useState<PercentChangeSettingsStateType>({...defaultSettings, ...props?.widget?.data?.settings});
  const settingsOnChange = (updatedState: PercentChangeSettingsStateType) => setSettingsState(updatedState);

  const [tabValue, setTabValue] = useState<number>(0);
  const handleTabChange = (event: React.SyntheticEvent<Element, Event>, newValue: number) => setTabValue(newValue);

  return (
    <TabbedDialog
      currentTab={tabValue}
      handleTabChange={handleTabChange}
      onCancel={() => props.setSettingsDialogIsOpen(false)}
      onConfirm={() => {
        updateWidget();
        props.setSettingsDialogIsOpen(false);
      }}
      open={true}
      tabs={[i18n.t("widget-settings.query-and-data"), i18n.t("display")]}
      title={i18n.t("widget-types.percent-change.settings.title")}>
      <>
        {/* Tab #1 */}
        {tabValue === 0 && (<>
          {props.queryInfo && (<>
            <SimpleFormLabel>{t("widget-types.percent-change.settings.time-field")}</SimpleFormLabel>
            <QueryFieldSelector
              allowedFieldTypes = {["timestamp"]}
              selectedFieldId = {timestampFieldId ?? null}
              requireAggSupported = {true}
              setSelectedFieldId = {(newFieldId: string) => {
                setTimestampFieldId(newFieldId);
              }}
              fields = {props.queryInfo.fields} />
            <SimpleFormLabel>{t("widget-types.percent-change.settings.data-field")}</SimpleFormLabel>
            <LeafAggSelector
              allowedFieldTypes = {["decimal", "integer", "keyword", "keywords"]}
              queryInfo = {props.queryInfo}
              aggs = {dataAgg}
              setAggs = {(agg: Agg | null, label: string | undefined) => {
                setDataAgg(agg ?? undefined);
              }} />
          </>)}
        </>)}
        {/* Tab #2 */}
        {tabValue === 1 &&
          <SplitSettingsWrapper>
            {/* Widget Graph Preview */}
            <PercentChangePreview settingsState={settingsState} />
            {/* Settings Options */}
            <SettingsOptions onChange={settingsOnChange} settingsState={settingsState}>
              <SettingsAccordion label={i18n.t("widget-settings.widget-title")}>
                {/* Widget Title */}
                <SettingsWidgetTitleSelect
                  keyid="widgetTitle"
                  label={i18n.t("title")}
                  showQueryOption={true}
                  widgetTitleValue={settingsState.widgetTitle} />
                  {settingsState.widgetTitle === 'custom' &&
                    <Stack direction="row" spacing={2} sx={{ marginLeft: 2 }}>
                      <Divider flexItem orientation="vertical" sx={{ width: "5px" }} />
                      <SettingsTextField
                        keyid="customWidgetTitle"
                        label={i18n.t("widget-settings.custom-text")}
                        value={settingsState.customWidgetTitle} />
                    </Stack>}
                  <SettingsToggleButton
                    keyid="widgetTitleAlignment"
                    label={i18n.t("widget-settings.alignment")}
                    optionsList={horizontalAlignmentOptions}
                    value={settingsState.widgetTitleAlignment} />
              </SettingsAccordion>
            </SettingsOptions>
          </SplitSettingsWrapper>}
      </>
    </TabbedDialog>
  )

}